/**
 * Breadcrumbs components.
 * @module components/theme/Breadcrumbs/Breadcrumbs
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';

import { getBreadcrumbs } from '@plone/volto/actions';
import { getBaseUrl, hasApiExpander } from '@plone/volto/helpers';

import { HeaderBreadcrumbs } from 'volto-ministerio-publico-federal/components/Breadcrumbs';

// Toda página interna leva breadcrumb; a exceção é a capa, que é a própria
// raiz do site e não tem hierarquia para situar.
const HIDDEN_BREADCRUMB_PATHS = ['/capa'];

interface BreadcrumbsComponentProps {
  getBreadcrumbs: (path: string) => void;
  pathname: string;
  root?: string;
  items: Array<{ title: string; url: string }>;
  /** Status do erro da última requisição de conteúdo, quando houve uma. */
  contentErrorStatus?: number;
}

/**
 * Breadcrumbs container class.
 */
export class BreadcrumbsComponent extends Component<BreadcrumbsComponentProps> {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    getBreadcrumbs: PropTypes.func.isRequired,
    pathname: PropTypes.string.isRequired,
    root: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
    contentErrorStatus: PropTypes.number,
  };

  componentDidMount() {
    if (!hasApiExpander('breadcrumbs', getBaseUrl(this.props.pathname))) {
      this.props.getBreadcrumbs(getBaseUrl(this.props.pathname));
    }
  }

  /**
   * Component will receive props
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps(nextProps: BreadcrumbsComponentProps) {
    if (nextProps.pathname !== this.props.pathname) {
      if (!hasApiExpander('breadcrumbs', getBaseUrl(this.props.pathname))) {
        this.props.getBreadcrumbs(getBaseUrl(nextProps.pathname));
      }
    }
  }

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    // O breadcrumb existe para situar a página na hierarquia do site; numa
    // 404 não há página para situar, e o que sobraria no rastro seria o
    // caminho inexistente que o visitante digitou. É o mesmo sinal que o
    // View.jsx do core usa para trocar o conteúdo pela página de erro
    // (`config.views.errorViews[404]`), então os dois não divergem.
    if (this.props.contentErrorStatus === 404) {
      return null;
    }
    if (HIDDEN_BREADCRUMB_PATHS.includes(this.props.pathname)) {
      return null;
    }
    return <HeaderBreadcrumbs props={this.props} />;
  }
}

export default compose(
  injectIntl,
  connect(
    (state: any) => ({
      items: state.breadcrumbs.items,
      root: state.breadcrumbs.root,
      contentErrorStatus: state.content?.get?.error?.status,
    }),
    { getBreadcrumbs },
  ),
)(BreadcrumbsComponent);
